<div *ngIf="isVisible" class="relative z-30" aria-labelledby="modal-title" role="dialog" aria-modal="true">
  <!-- Background backdrop -->
  <div class="fixed inset-0 bg-secondary-900 bg-opacity-50 backdrop-blur-sm transition-opacity"></div>

  <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
    <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
      <!-- Modal panel -->
      <div class="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">

        <!-- Close button -->
        <div class="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
          <button type="button" (click)="onClose()" class="rounded-md bg-white text-gray-400 hover:opacity-90 focus:outline-none">
            <span class="sr-only">Close</span>
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="px-4 py-4 lg:px-6 lg:py-6">
          <!-- Error State -->
          <div *ngIf="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            <p class="font-semibold">Error loading company:</p>
            <p>{{ error }}</p>
          </div>

          <!-- Company Details -->
          <div class="space-y-6">

            <!-- Company Header -->
            <div class="flex items-center space-x-4 pb-6 border-b border-gray-100">
              <div class="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0" [class.animate-pulse]="!company">
                <span class="text-primary text-xl font-medium" *ngIf="company">{{ getInitials(company.name || '') }}</span>
                <div *ngIf="!company" class="w-8 h-8 rounded-full animate-pulse"></div>
              </div>
              <div class="flex-1">
                <ng-container *ngIf="company; else headerSkeleton">
                  <h4 class="text-xl font-bold text-gray-900">{{ company.name || '—' }}</h4>
                  <p class="text-gray-500 text-sm">{{ company.industry || 'No industry specified' }}</p>
                  <p class="text-gray-500 text-sm">{{ company.city && company.country ? company.city + ', ' + company.country : 'No location specified' }}</p>
                </ng-container>
                <ng-template #headerSkeleton>
                  <div class="h-6 bg-gray-100 rounded animate-pulse mb-2"></div>
                  <div class="h-4 bg-gray-100 rounded animate-pulse mb-1 w-3/4"></div>
                  <div class="h-4 bg-gray-100 rounded animate-pulse w-1/2"></div>
                </ng-template>
              </div>
            </div>

            <!-- Contact Information -->
            <div>
              <h5 class="text-lg font-semibold text-gray-700 mb-4">Contact Information</h5>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <ng-container *ngIf="company; else websiteSkeleton">
                    <ng-container *ngIf="company.url; else noWebsite">
                      <a [href]="company.url" target="_blank" class="text-primary hover:text-primary/80 underline inline-flex items-center">
                        {{ company.url }}
                        <svg class="ml-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </ng-container>
                    <ng-template #noWebsite>
                      <span class="text-gray-500">Not provided</span>
                    </ng-template>
                  </ng-container>
                  <ng-template #websiteSkeleton>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-2/3"></div>
                  </ng-template>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <ng-container *ngIf="company; else phoneSkeleton">
                    <ng-container *ngIf="company.phone_number; else noPhone">
                      <a [href]="'tel:' + company.phone_number" class="text-primary hover:text-primary/80 underline">{{ company.phone_number }}</a>
                    </ng-container>
                    <ng-template #noPhone>
                      <span class="text-gray-500">Not provided</span>
                    </ng-template>
                  </ng-container>
                  <ng-template #phoneSkeleton>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-1/2"></div>
                  </ng-template>
                </div>
                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
                  <ng-container *ngIf="company; else linkedinSkeleton">
                    <ng-container *ngIf="company.linkedin; else noLinkedIn">
                      <a [href]="company.linkedin" target="_blank" class="text-primary hover:text-primary/80 underline inline-flex items-center">
                        {{ company.linkedin }}
                        <svg class="ml-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </ng-container>
                    <ng-template #noLinkedIn>
                      <span class="text-gray-500">Not provided</span>
                    </ng-template>
                  </ng-container>
                  <ng-template #linkedinSkeleton>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-3/4"></div>
                  </ng-template>
                </div>
              </div>
            </div>

            <!-- Company Information -->
            <div>
              <h5 class="text-lg font-semibold text-gray-700 mb-4">Company Information</h5>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                  <ng-container *ngIf="company; else industrySkeleton">
                    <p class="text-gray-900">{{ company.industry || '—' }}</p>
                  </ng-container>
                  <ng-template #industrySkeleton>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-1/2"></div>
                  </ng-template>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Company ID</label>
                  <ng-container *ngIf="company; else identifierSkeleton">
                    <p class="text-gray-900">{{ company.company_identifier || '—' }}</p>
                  </ng-container>
                  <ng-template #identifierSkeleton>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-1/3"></div>
                  </ng-template>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Employees</label>
                  <ng-container *ngIf="company; else employeesSkeleton">
                    <p class="text-gray-900">{{ formatEmployeeCount(company.number_of_employees) }}</p>
                  </ng-container>
                  <ng-template #employeesSkeleton>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-1/4"></div>
                  </ng-template>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Revenue</label>
                  <ng-container *ngIf="company; else revenueSkeleton">
                    <p class="text-gray-900">{{ company.formatted_revenue || '—' }}</p>
                  </ng-container>
                  <ng-template #revenueSkeleton>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-1/3"></div>
                  </ng-template>
                </div>
              </div>
            </div>

            <!-- Associated Contacts -->
            <div>
              <h5 class="text-lg font-semibold text-gray-700 mb-4">Associated Contacts</h5>
              <div class="grid grid-cols-1 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Contacts</label>
                  <ng-container *ngIf="company; else contactsSkeleton">
                    <div *ngIf="company.company_contacts && company.company_contacts.length > 0; else noContacts" class="space-y-2">
                      <div *ngFor="let companyContact of company.company_contacts" class="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <div class="size-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span class="text-primary text-sm font-medium">
                            {{ (companyContact.contact?.name?.charAt(0) || '') + (companyContact.contact?.surname?.charAt(0) || '') }}
                          </span>
                        </div>
                        <div class="flex-1">
                          <p class="text-sm font-medium text-gray-900">
                            {{ companyContact.contact?.name || 'Unknown' }} {{ companyContact.contact?.surname || 'Contact' }}
                          </p>
                          <div class="flex items-center space-x-2">
                            <span class="text-xs text-gray-500" *ngIf="companyContact.is_default === 1">Primary Contact</span>
                            <p class="text-xs text-gray-500">{{ companyContact.contact?.email || 'No email' }}</p>
                            <a *ngIf="companyContact.contact?.phone_number" 
                               [href]="'tel:' + companyContact.contact.phone_number" 
                               class="text-xs text-primary hover:text-primary/80 underline">
                              {{ companyContact.contact.phone_number }}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                    <ng-template #noContacts>
                      <p class="text-gray-500">No contacts associated</p>
                    </ng-template>
                  </ng-container>
                  <ng-template #contactsSkeleton>
                    <div class="space-y-2">
                      <div class="h-16 bg-gray-100 rounded-lg animate-pulse"></div>
                      <div class="h-16 bg-gray-100 rounded-lg animate-pulse"></div>
                    </div>
                  </ng-template>
                </div>
              </div>
            </div>

            <!-- Location Information -->
            <div>
              <h5 class="text-lg font-semibold text-gray-700 mb-4">Location</h5>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <ng-container *ngIf="company; else addressSkeleton">
                    <p class="text-gray-900">{{ getFullAddress(company) }}</p>
                  </ng-container>
                  <ng-template #addressSkeleton>
                    <div class="space-y-1">
                      <div class="h-4 bg-gray-100 rounded animate-pulse w-full"></div>
                      <div class="h-4 bg-gray-100 rounded animate-pulse w-2/3"></div>
                    </div>
                  </ng-template>
                </div>
              </div>
            </div>

            <!-- About Company -->
            <div>
              <h5 class="text-lg font-semibold text-gray-700 mb-4">About Company</h5>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <ng-container *ngIf="company; else aboutSkeleton">
                  <p class="text-gray-900 whitespace-pre-wrap">{{ company.about_company || 'No company description provided.' }}</p>
                </ng-container>
                <ng-template #aboutSkeleton>
                  <div class="space-y-2">
                    <div class="h-4 bg-gray-100 rounded animate-pulse"></div>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-5/6"></div>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-3/4"></div>
                  </div>
                </ng-template>
              </div>
            </div>

            <!-- Timestamps -->
            <div class="border-t border-gray-100 pt-6">
              <h5 class="text-lg font-semibold text-gray-700 mb-4">Record Information</h5>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Created</label>
                  <ng-container *ngIf="company; else createdSkeleton">
                    <p class="text-gray-600">{{ formatDate(company.created_at || '') }}</p>
                  </ng-container>
                  <ng-template #createdSkeleton>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-3/4"></div>
                  </ng-template>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                  <ng-container *ngIf="company; else updatedSkeleton">
                    <p class="text-gray-600">{{ formatDate(company.updated_at || '') }}</p>
                  </ng-container>
                  <ng-template #updatedSkeleton>
                    <div class="h-4 bg-gray-100 rounded animate-pulse w-3/4"></div>
                  </ng-template>
                </div>
              </div>
            </div>

          </div>
        </div>

        <!-- Action button -->
        <div class="px-4 sm:px-6 pb-6">
          <button
            type="button"
            (click)="onClose()"
            class="w-full rounded-md px-4 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-transparent">
            Close
          </button>
        </div>

      </div>
    </div>
  </div>
</div>
